
import Vue from "vue";
import { Prop, Component, PropSync, Watch } from "vue-property-decorator";
import TradingBotStats from "@/models/TradingBotStats";
import BaseView from "@/views/BaseView.vue";
import OpenOrder from "@/models/OpenOrder";
import { Emit } from "@/api/apiHelper";

/**
 * Possible archiving options for a trading bot
 */
export enum ArchiveOptions {
  CloseOpenOrders,
  SellActivePositions,
}

@Component
export default class ArchiveBotDialog extends BaseView {
  // 📦 Bot stats of the bot that is to be archived
  @Prop({ default: undefined })
  public stats: TradingBotStats | null = null;

  // 🚩 Flag which determines whether the dialog should be shown
  @PropSync("showDialog", { type: Boolean })
  public show!: boolean;

  // 📦 Object which holds all the details for the open orders functionality
  private openOrdersCheckbox = {
    visible: false,
    editable: false,
    value: true,
  };

  // 📦 Object which holds all the details for the active positions functionality
  private activePositionsCheckbox = {
    visible: false,
    editable: false,
    value: true,
  };

  // 🚩 Flag for whether the bot is being archived
  private isArchiving: boolean = false;

  @Watch("stats")
  onBotStatsChanged(val: TradingBotStats, oldVal?: TradingBotStats) {
    // Reset view
    this.openOrdersCheckbox = {
      visible: false,
      editable: true,
      value: true,
    };
    this.activePositionsCheckbox = {
      visible: false,
      editable: true,
      value: true,
    };

    // Check if there are any open orders
    if (!!val.open_orders && val.open_orders?.length > 0) {
      if (this.isStopLossOrderPlaced(val.open_orders)) {
        this.openOrdersCheckbox.editable = false;
      }
      this.openOrdersCheckbox.visible = true;
    }

    // Check whether there is an open position
    if (val.tokBalance != null && val.tokBalance > 0) {
      this.activePositionsCheckbox.visible = true;
    }

    // If there is a stop-loss placed => make
    if (this.isStopLossOrderPlaced(val.open_orders)) {
      this.activePositionsCheckbox.visible = true;
      this.activePositionsCheckbox.editable = false;
    }
  }

  /**
   * 🔨 Checks whether any stop-loss order was already placed
   */
  private isStopLossOrderPlaced(orders: OpenOrder[]): boolean {
    return (
      orders.find((openOrder: OpenOrder) => openOrder.type == "STOPLOSS") !=
      undefined
    );
  }

  /*
   * 🚮 Archives the selected trading bot
   */
  private async archiveTradingBot() {
    this.isArchiving = true;
    Vue.prototype.$api
      .delete("/api/v1/createbot", {
        uid: this.stats?.uid,
        close_open_orders: this.isStopLossOrderPlaced(this.stats?.open_orders)
          ? true
          : this.openOrdersCheckbox.value,
        sell_active: this.isStopLossOrderPlaced(this.stats?.open_orders)
          ? true
          : this.activePositionsCheckbox.value,
      }, Emit.All, true)
      .then((respone: any) => {
        this.isArchiving = false;
        super.showSuccessSnackbar("Your bot was successfully archived!");
        this.$store.dispatch("removeBotByIdLocally", this.stats?.uid);
        this.$router.push("dashboard");
      })
      .finally(() => (this.isArchiving = false));
  }
}
